import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import { Button } from '@material-ui/core';

import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSubmitWrapper } from '../../../services/FormSaveService';
import { useFormDeleteWrapper } from '../../../services/FormDeleteService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useIsSysAdminUser } from '../../../stores/SessionStore';

import { usePlmService } from '../../../services/PlmService';
import { usePlms } from '../../../stores/PlmStateStore';

import {
    PLM_NAME_FIELD,
    PLM_HOST_FIELD,
    PLM_SECRET_FIELD,
    PLM_DESCRIPTION_FIELD,
    PLM_CREATED_AT_FIELD,
    PLM_UPDATED_AT_FIELD,
    PLM_PAGE_TITLE,
    PLM_UI_LABEL,
    BASE_URL,
    UNCHANGED_SECRET,
    PLM_SECRET_HELP,
    PLM_AUTO_ACCESS,
} from './PlmConstants';

import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import NgpTextField from '../../form/NgpTextField';
import NgpTextarea from '../../form/NgpTextarea';
import NgpPasswordField from '../../form/NgpPasswordField';

import { useV3Api } from './V3ApiHelper';
import AuthRoleGuard from '../../auth/AuthRoleGuard';
import NgpToggle from '../../form/NgpToggle';

const functionName = AdminPages.Plms;
const goToUrl = '..';
const defaultValues = {
    [PLM_NAME_FIELD]: '',
    [PLM_HOST_FIELD]: '',
    [PLM_DESCRIPTION_FIELD]: '',
    [PLM_SECRET_FIELD]: UNCHANGED_SECRET,
    [PLM_UPDATED_AT_FIELD]: new Date(),
    [PLM_CREATED_AT_FIELD]: new Date(),
};

export default function PlmAdminDetail() {
    const [isSysAdminUser] = useIsSysAdminUser();
    const recordId = useEntityIdParam();
    const plmService = usePlmService();
    const [, /* Not used */ plmActions] = usePlms();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, getValues, setError, handleSubmit } = methods;
    const functionBreadCrumbs = [{ link: goToUrl, text: i18n._(t`${PLM_PAGE_TITLE}`) }];
    const newBreadCrumbText = i18n._(t`New ${PLM_UI_LABEL}`);

    const v3 = useV3Api();

    const onGetError = v3.useFirstAlert;
    const onSubmitError = v3.useSetErrors(setError);
    const onComplete = useCallback(
        (response) => {
            plmService.getPlms().then(plmActions.addPlms);
            return response;
        },
        [plmService, plmActions]
    );

    const { initialized, finding, formData } = v3.useGetOne({
        url: BASE_URL,
        recordId,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper(onGetError, goToUrl),
    });

    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, PLM_NAME_FIELD, newBreadCrumbText);
    const beforeSubmit = (data) => {
        const updatedSecret = getValues(PLM_SECRET_FIELD);
        if (updatedSecret === UNCHANGED_SECRET) {
            _.unset(data, PLM_SECRET_FIELD);
        }
        return data;
    };
    const handleFormSubmit = v3.useUpdateOne({
        url: BASE_URL,
        recordId,
        beforeSubmit,
        afterSubmit: onComplete,
        errorHandler: errorWrapper(onSubmitError),
    });
    const deleteFunction = v3.useDeleteOne({
        url: BASE_URL,
        recordId,
        afterSubmit: onComplete,
        errorHandler: errorWrapper(onSubmitError),
    });
    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction,
            onDeleteGoTo: goToUrl,
        })();
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);
    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                isSysAdminUser && (
                    <Actions>
                        {!_.isEmpty(recordId) && (
                            <ConfirmDeleteButton handleConfirmedDelete={handleConfirmedDelete} plural={false} />
                        )}
                        <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                            <Trans>Save</Trans>
                        </Button>
                    </Actions>
                )
            }
        >
            <AuthRoleGuard allowedRoles={['super']}>
                <FormProvider {...methods}>
                    <NgpForm
                        submitFunction={handleSubmitFunction}
                        initialized={initialized}
                        loading={finding || saving || deleting}
                    >
                        {isSysAdminUser && (
                            <FormSection title={i18n._(t`${PLM_UI_LABEL} Details`)}>
                                <>
                                    <NgpTextField
                                        fieldName={PLM_NAME_FIELD}
                                        label={i18n._(t`${PLM_UI_LABEL} Name`)}
                                        required
                                        inputProps={{ size: 40, maxLength: 128 }}
                                    />
                                    <NgpToggle
                                        fieldName={PLM_AUTO_ACCESS}
                                        label={
                                            <Trans>
                                                Make this local manager automatically accessible for new external users
                                            </Trans>
                                        }
                                    />
                                    <NgpTextField
                                        fieldName={PLM_HOST_FIELD}
                                        label={i18n._(t`${PLM_UI_LABEL} Host`)}
                                        required
                                        inputProps={{ size: 40, maxLength: 128 }}
                                    />

                                    <NgpTextarea
                                        fieldName={PLM_DESCRIPTION_FIELD}
                                        label={i18n._(t`${PLM_UI_LABEL} Description`)}
                                        cols={40}
                                        inputProps={{ size: 255 }}
                                    />

                                    <NgpPasswordField
                                        fieldName={PLM_SECRET_FIELD}
                                        label={i18n._(t`${PLM_UI_LABEL} Secret`)}
                                        inputProps={{ size: 40 }}
                                        required
                                        helpTipText={PLM_SECRET_HELP}
                                    />
                                </>
                            </FormSection>
                        )}
                    </NgpForm>
                </FormProvider>
            </AuthRoleGuard>
        </AdminPage>
    );
}
